import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isMyIdSuccess, type VerifyIdentificationResponse } from '@asko/shared';
import { ApiError, api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Mijoz MyID sahifasidan qaytadigan joy.
 *
 * MyID `redirect_uri` ni O'ZIDA ro'yxatdan o'tkazadi va u bitta bo'ladi,
 * shuning uchun manzilga identifikatsiya raqamini qo'shib bo'lmaydi.
 * Qaysi mijoz qaytganini brauzerning o'zi eslab qoladi: `/m/:token`
 * sahifasi ketishdan oldin tokenni `sessionStorage` ga yozadi.
 */

export function MyIdCallbackPage() {
  const { t, apiMessage, myIdMessage } = useI18n();
  const [params] = useSearchParams();
  const [error, setError] = useState<ApiError | null>(null);
  const [failure, setFailure] = useState<VerifyIdentificationResponse | null>(null);
  const [done, setDone] = useState(false);

  // `auth_code` bir martalik. Sahifa qayta chizilsa yoki mijoz orqaga
  // qaytsa, o'sha kod ikkinchi marta yuborilib, MyID uni rad etardi —
  // ekranda esa texnik xato chiqardi (jonli mijoz, 25.08).
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;

    const token = sessionStorage.getItem('myid:handoff');
    const code = params.get('auth_code') ?? params.get('code');

    if (!token || !code) {
      setError(null);
      setFailure({
        status: 'FAILED',
        client: null,
        resultCode: null,
        message: '',
        retryable: true,
      });
      return;
    }

    api
      .post<VerifyIdentificationResponse>(`/myid/handoff/${token}/myid`, { code })
      .then((result) => {
        if (result.status === 'SUCCESS') {
          sessionStorage.removeItem('myid:handoff');
          setDone(true);
        } else {
          setFailure(result);
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err : null));
  }, [params]);

  return (
    <div className="mobile-shell">
      <main className="mobile-shell__body">
        <div className="card mobile-done">
          {done ? (
            <>
              <div className="mobile-done__mark" aria-hidden="true">
                ✓
              </div>
              <h1 className="section-title">{t('mobile.doneTitle')}</h1>
              <p className="section-hint">{t('mobile.doneHint')}</p>
            </>
          ) : failure ? (
            <>
              {/*
                MyID «o'tdi» deb javob berib, natija baribir yakunlanmasligi
                mumkin (masalan, rasm olinmadi). Bunday paytda «o'tmadi»
                deb yozib bo'lmaydi — pastdagi matnning o'ziga zid bo'ladi.
              */}
              <h1 className="section-title">
                {t(
                  isMyIdSuccess(failure.resultCode)
                    ? 'mobile.incompleteTitle'
                    : 'mobile.failedTitle',
                )}
              </h1>
              <p className="section-hint">
                {myIdMessage(failure.resultCode, failure.message) || t('mobile.failedHint')}
              </p>
            </>
          ) : error ? (
            <div className="alert alert--error" role="alert">
              {apiMessage(error)}
            </div>
          ) : (
            <>
              <div className="spinner" aria-label={t('common.loading')} />
              <p className="section-hint">{t('mobile.checking')}</p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
